import React from 'react';



/**
 * Avatar component with online status indicator
 * Displays user/team profile picture or initials
 *
 * @param {Object} props
 * @param {string} props.src - Profile picture URL (optional)
 * @param {string} props.name - User/team name (used for initials if no src)
 * @param {number} props.size - Avatar size in pixels (default: 40)
 * @param {boolean} props.isOnline - Show online status indicator
 * @param {string} props.status - Status type: 'online', 'away', 'offline'
 * @param {boolean} props.clickable - Make avatar clickable
 * @param {function} props.onClick - Click handler
 * @param {string} props.className - Additional CSS classes
 */
const Avatar = ({
  src,
  name = '',
  size = 40,
  isOnline = false,
  status = 'offline',
  clickable = false,
  onClick,
  className = ''
}) => {
  // Generate initials from name (first letter of first two words)
  const getInitials = (name) => {
    if (!name) return '?';

    const words = name.trim().split(' ').filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0][0].toUpperCase();

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Generate background color from name (consistent color per name)
  const getBackgroundColor = (name) => {
    if (!name) return 'var(--talk-bg-panel)';

    const colors = [
      '#00A884', // Primary green
      '#0C5097', // Blue
      '#7C3AED', // Purple
      '#DC2626', // Red
      '#EA580C', // Orange
      '#0891B2', // Cyan
      '#16A34A', // Green
      '#CA8A04', // Yellow
    ];

    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const avatarStyle = {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${size * 0.4}px`,
    backgroundColor: src ? 'transparent' : getBackgroundColor(name)
  };

  const showStatusIndicator = isOnline || status !== 'offline';
  const statusClass = `avatar-status avatar-status-${status}`;

  return (
    <div
      className={`talk-avatar ${clickable ? 'talk-avatar-clickable' : ''} ${className}`}
      style={avatarStyle}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e);
        }
      } : undefined}
      aria-label={clickable ? `${name}'s profile` : name}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className="talk-avatar-image"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}

      <div
        className="talk-avatar-initials"
        style={{ display: src ? 'none' : 'flex' }}
      >
        {getInitials(name)}
      </div>

      {showStatusIndicator && (
        <span
          className={statusClass}
          aria-label={`${status} status`}
        />
      )}
    </div>
  );
};

export default Avatar;
